'use client'

import { useState, useEffect } from 'react'

// ============================================================================
// Types
// ============================================================================

interface TreeNode {
  id: string
  titulo: string
  status: string
  is_parent: boolean
  level: number
  execution_order: number
  depends_on: string[]
}

interface SubtaskTreeProps {
  taskId: string
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function SubtaskTree({ taskId, className = '' }: SubtaskTreeProps) {
  const [tree, setTree] = useState<TreeNode[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTree()
  }, [taskId])

  const fetchTree = async () => {
    try {
      const response = await fetch(`https://api.lldonha.com/api/tasks/${taskId}/tree`)
      const data = await response.json()

      if (data.success) {
        setTree(data.tree)
      }
    } catch (error) {
      console.error('Error fetching tree:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        Carregando hierarquia...
      </div>
    )
  }

  if (tree.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Nenhuma subtask encontrada
      </p>
    )
  }

  const statusIcons = {
    pending: '⏸️',
    running: '🔄',
    done: '✅',
    error: '❌',
    needs_review: '📝',
    approved: '✅'
  }

  const statusColors = {
    pending: 'text-gray-500',
    running: 'text-blue-600',
    done: 'text-green-600',
    error: 'text-red-600',
    needs_review: 'text-yellow-600',
    approved: 'text-green-600'
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
        📊 Hierarquia de Tasks:
      </h4>

      {tree.map((node) => (
        <div
          key={node.id}
          className="flex items-start gap-2 text-sm"
          style={{ paddingLeft: `${node.level * 1.5}rem` }}
        >
          {/* Connector */}
          {node.level > 0 && (
            <span className="text-gray-400">
              {node.level === 1 ? '├─' : '└─'}
            </span>
          )}

          {/* Status Icon */}
          <span className={statusColors[node.status as keyof typeof statusColors]}>
            {statusIcons[node.status as keyof typeof statusIcons]}
          </span>

          {/* Title */}
          <span className={`flex-1 ${node.is_parent ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
            {node.titulo}
          </span>

          {/* Execution Order */}
          <span className="text-xs text-gray-500">
            order: {node.execution_order}
          </span>

          {/* Dependencies */}
          {node.depends_on && node.depends_on.length > 0 && (
            <span className="text-xs text-gray-500" title={`Depende de: ${node.depends_on.join(', ')}`}>
              🔗 {node.depends_on.length}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
